/**
 * MM Newsletter
 * @description Inscription Klaviyo au submit du formulaire newsletter (footer + menu mobile).
 *              Le formulaire reste un `{% form 'customer' %}` Shopify intact : on écoute le
 *              submit sans le bloquer (captcha Shopify préservé) et on pousse en parallèle le
 *              profil dans la liste Klaviyo via l'endpoint client (CORS OK, clé publique).
 *              Config : data-attributes posés par `snippets/mm-newsletter.liquid`.
 *              La locale de la boutique visitée (langue + pays) est envoyée avec le profil pour
 *              que Klaviyo dérive `$locale_language` immédiatement (tri des listes Locale_*).
 * @version 1.1.0
 */

const MM_Newsletter = (() => {
	'use strict';

	// ==========================================================================
	// Configuration
	// ==========================================================================
	const CONFIG = {
		isDevelopment:
			window.location.hostname === 'localhost' || window.location.hostname.includes('.myshopify.com'),
		selectors: {
			root: '[data-mm-newsletter]',
			form: '.mm-newsletter-form',
			email: 'input[type="email"]'
		},
		klaviyo: {
			endpoint: 'https://a.klaviyo.com/client/subscriptions/',
			revision: '2026-07-15'
		}
	};

	// ==========================================================================
	// Logging
	// ==========================================================================
	const log = (level, message, data = {}) => {
		if (!CONFIG.isDevelopment && level === 'info') return;
		const logFn = console[level] || console.log;
		logFn(`[MM_Newsletter] ${message}`, data);
	};

	// ==========================================================================
	// Locale
	// ==========================================================================
	/**
	 * Locale IETF BCP 47 de la boutique visitée (ex. "fr-FR", "de-FR").
	 * Source : globals `Shopify.locale` / `Shopify.country` (posés par Shopify sur chaque page),
	 * repli sur l'attribut `lang` de <html>. Retourne null si rien n'est disponible.
	 */
	const getStorefrontLocale = () => {
		const shopify = window.Shopify || {};
		const language = (shopify.locale || document.documentElement.lang || '').trim().toLowerCase();
		if (!language) return null;
		const country = (shopify.country || '').trim().toUpperCase();
		if (!country || language.includes('-')) return language;
		return `${language}-${country}`;
	};

	// ==========================================================================
	// Klaviyo
	// ==========================================================================
	const buildSubscriptionsUrl = (companyId) => {
		const url = new URL(CONFIG.klaviyo.endpoint);
		url.searchParams.set('company_id', companyId);
		return url.toString();
	};

	const buildSubscriptionPayload = (email, listId, source) => {
		const locale = getStorefrontLocale();
		const language = locale ? locale.split('-')[0] : null;
		return {
			data: {
				type: 'subscription',
				attributes: {
					custom_source: source,
					profile: {
						data: {
							type: 'profile',
							attributes: {
								email,
								...(locale ? { locale } : {}),
								properties: {
									$source: source,
									signup_source: source,
									...(language ? { signup_language: language } : {})
								},
								subscriptions: {
									email: { marketing: { consent: 'SUBSCRIBED' } }
								}
							}
						}
					}
				},
				relationships: {
					list: { data: { type: 'list', id: listId } }
				}
			}
		};
	};

	/** POST Klaviyo « fire and forget » : une erreur ne doit jamais bloquer le submit Shopify */
	const subscribe = (email, { publicKey, listId, source }) =>
		fetch(buildSubscriptionsUrl(publicKey), {
			method: 'POST',
			keepalive: true,
			headers: {
				'Content-Type': 'application/json',
				revision: CONFIG.klaviyo.revision
			},
			body: JSON.stringify(buildSubscriptionPayload(email, listId, source))
		})
			.then((response) => {
				// 202 Accepted, corps vide
				if (response.status === 202 || response.ok) {
					log('info', 'Subscription accepted', { source });
					return;
				}
				log('error', 'Subscription failed', { status: response.status });
			})
			.catch((error) => {
				log('error', 'Subscription error', { error: error.message });
			});

	// ==========================================================================
	// Handlers
	// ==========================================================================
	const handleSubmit = (e) => {
		const form = e.target.closest(CONFIG.selectors.form);
		if (!form) return;

		const root = form.closest(CONFIG.selectors.root);
		if (!root) return;

		const { klaviyoPublicKey: publicKey, klaviyoListId: listId, klaviyoSource: source } = root.dataset;
		if (!publicKey || !listId) return;

		const email = form.querySelector(CONFIG.selectors.email)?.value?.trim() || '';
		if (!email) return;

		// Pas de preventDefault : le submit Shopify (et son captcha) doit suivre son cours normal.
		// `keepalive` laisse la requête Klaviyo se terminer malgré la navigation.
		subscribe(email, { publicKey, listId, source });
	};

	// ==========================================================================
	// Init
	// ==========================================================================
	const init = () => {
		log('info', 'Initializing...');
		document.addEventListener('submit', handleSubmit, true);
	};

	// ==========================================================================
	// Public API
	// ==========================================================================
	return { init };
})();

// Auto-init on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', MM_Newsletter.init);
} else {
	MM_Newsletter.init();
}
